<template>
  <parent>
    <div>
      <div class="mb-2">
        <b-button
          variant="bg-white"
          class="shadow"
          @click="$router.push({ name: 'live-encoder' })"
        >
          <GjIcon name="ArrowLeft" />
          Back
        </b-button>
      </div>
      <loading-page v-if="gettingLiveJob && !!!liveJobData" />
      <div
        v-else
        class="live-job-settings"
      >
        <paper>
          <template v-slot:title>
            <p class="mb-0">
              Overview
            </p>
            <a
              v-clipboard:copy="liveJobData.name"
              v-clipboard:success="copySuccess"
              class="d-flex align-items-center cursor-pointer fit-content"
            >
              <span>{{ liveJobData.name }}</span>
              <span
                class="text-secondary"
                style="margin-left: 13px"
              >
                <GjIcon
                  name="Copy"
                  size="20"
                />
              </span>
            </a>
          </template>
          <div class="px-2">
            <div class="mb-1">
              <div class="d-flex justify-content-between align-items-center flex-wrap">
                <div
                  class="position-relative job-status"
                  :class="jobStatusClass"
                >
                  {{
                    getObjectOptionsText(
                      liveJob.liveJobStatusType,
                      liveJob.status,
                    )
                  }}
                  <b-icon-circle-fill
                    :animation="playerAvailable ? 'throb' : ''"
                  />
                </div>
                <div class="d-flex justify-content-end">
                  <b-button
                    v-if="playerAvailable"
                    variant="bg-white"
                    class="ml-2"
                    :disabled="settingTranscodingStatus"
                    @click="updateTranscoding"
                  >
                    {{ liveJobData.status === liveJobData.liveJobStatusType.Processing ? 'Stop' : 'Start' }}
                    <GjIcon
                      :key="`${liveJobData.liveJobStatusType.Live}${settingTranscodingStatus}${gettingLiveJob}`"
                      class="icon-right"
                      :name="transcodingStatusIcon"
                    />
                  </b-button>

                  <b-button
                    variant="bg-white"
                    class="ml-2"
                    :class="[liveJobData.status === liveJobData.liveJobStatusType.Completed ? 'd-none' : '']"
                    @click="fetchLiveJob"
                  >
                    Refresh
                    <GjIcon
                      class="icon-right"
                      name="Refresh"
                    />
                  </b-button>
                </div>
              </div>

              <div
                class="pt-2 row"
                :class="[liveJobData.status === liveJobData.liveJobStatusType.Completed ? 'd-none' : '']"
              >
                <div class="col-12 col-md-7 col-lg-7 mb-2 mb-md-0">
                  <div
                    class="d-flex label-text justify-content-between flex-wrap"
                  >
                    <span class="">Playback Preview</span>
                  </div>
                  <div
                    v-if="liveJobData.status === liveJobData.liveJobStatusType.Live && liveJobData.record"
                    style="position:absolute;top:30px;right:18px;z-index:100;padding:4px"
                  >
                    <b-icon-record-circle
                      animation="throb"
                      style="color:red"
                    />
                    <span
                      style="color:red;font-weight:semi-bold;font-size:13px"
                    >
                      Recording</span>
                  </div>
                  <vp-player
                    :file-path="filePathComputed"
                    player-id="vp-player"
                    class="shadow"
                    :is-live="true"
                  />
                </div>
                <div
                  class="col-12 col-md-5 col-lg-5 d-flex flex-column"
                  style="height:400px;"
                >
                  <job-statistics
                    :stats="jobStatistics"
                    :active="playerAvailable"
                    :loading="gettingLiveJob"
                    @refresh="fetchLiveJob"
                  />
                </div>
              </div>
            </div>

            <paper class="inner-paper">
              <template v-slot:title>
                <p class="mb-0">
                  Details
                </p>
              </template>
              <div class="row">
                <div class="col-12 col-lg-12">
                  <b-form-group
                    v-if="liveJob.streamType === 0"
                    class="details mb-0"
                    label="Primary Endpoint"
                    label-cols="12"
                    label-cols-md="2"
                  >
                    <a
                      v-clipboard:copy="primaryEndpoint"
                      v-clipboard:success="copySuccess"
                      class="
                        d-flex
                        align-items-center
                        cursor-pointer
                        fit-content
                      "
                    >
                      <span>{{ primaryEndpoint }}</span>
                      <span
                        class="text-secondary"
                        style="margin-left: 13px"
                      >
                        <GjIcon
                          name="Copy"
                          size="20"
                        />
                      </span>
                    </a>
                  </b-form-group>
                  <b-form-group
                    v-if="liveJob.streamType === 0"
                    class="details mb-0"
                    label="Stream Key"
                    label-cols="12"
                    label-cols-md="2"
                  >
                    <text-hidden
                      :show-password="showPassword"
                      :text="liveJobKey"
                      white-background
                      @toggle="showPassword = !showPassword"
                    />
                  </b-form-group>

                  <b-form-group
                    class="details mb-0"
                    label="Template Name"
                    label-cols="12"
                    label-cols-md="2"
                  >
                    <router-link :to="{ name: 'job-templates-view', params: { id: liveJobData.jobTemplateId, projectId: $route.params.projectId }}">
                      <span>{{ liveJobData.jobTemplateName }} </span>
                    </router-link>
                    <span
                      class="text-secondary"
                      style="margin-left: 13px"
                    >
                      <GjIcon
                        name="Redirect"
                        size="20"
                      />
                    </span>
                  </b-form-group>
                  <b-form-group
                    v-if="liveJob.streamType === 1"
                    class="details mb-0"
                    label="File URL"
                    label-cols="12"
                    label-cols-md="2"
                  >
                    <a
                      v-clipboard:copy="liveJob.input.filePath"
                      v-clipboard:success="copySuccess"
                      class="
                        d-flex
                        align-items-center
                        cursor-pointer
                        fit-content
                      "
                    >
                      <span>{{ liveJob.input.filePath }}</span>
                      <span
                        class="text-secondary"
                        style="margin-left: 13px"
                      >
                        <GjIcon
                          name="Copy"
                          size="20"
                        />
                      </span>
                    </a>
                  </b-form-group>
                  <b-form-group
                    v-if="liveJobData.record"
                    class="details mb-0"
                    label="Record Type"
                    label-cols="12"
                    label-cols-md="2"
                  >
                    <a
                      class="
                        d-flex
                        align-items-center
                        cursor-pointer
                        fit-content
                      "
                    >
                      <span>{{ liveJobData.recordType === 0 ? 'Record All' : 'Record Hourly' }}</span>
                    </a>
                  </b-form-group>

                  <b-form-group
                    class="details mb-0"
                    label="Has Relay"
                    label-cols="12"
                    label-cols-md="2"
                  >
                    <a
                      class="
                        d-flex
                        align-items-center
                        cursor-pointer
                        fit-content
                      "
                    >
                      <span>{{ liveJobData.hasRelay ? 'Yes' : 'No' }}</span>
                    </a>
                  </b-form-group>
                </div>
              </div>
            </paper>

            <collapsable
              v-if="liveJobCuttedVideo.length !== 0"
              ref="trimmedVideo"
              class="collapsable"
              title="Trimmed Video"
            >
              <div class="d-flex align-items-start justify-content-start flex-wrap">
                <video
                  :src="liveJobCuttedVideo.mP4"
                  controls
                  width="500px"
                />
                <div class="d-sm-block d-lg-block mt-0 ml-3">
                  <h5>
                    Cliped video link
                  </h5>
                  <a :href="liveJobCuttedVideo.mP4"><strong>mp4:</strong> {{ liveJobCuttedVideo.mP4 }}</a>

                </div>
              </div>
            </collapsable>

            <collapsable
              class="collapsable"
              header-class="px-0"
              title="Output Groups"
            >
              <template v-if="liveJobData.groups.length > 0">
                <output-group
                  v-for="(group, index) in liveJobData.groups"
                  :key="index"
                  :group="group"
                />
              </template>
              <template v-else>
                No output groups available.
              </template>
            </collapsable>

            <collapsable
              class="collapsable"
              title="Tags"
            >
              <b-table
                ref="table"
                :items="liveJob.tags"
                show-empty
                :fields="fields"
                responsive
                class="loaded border-default border-b border-r border-l rounded"
              />
            </collapsable>

            <collapsable
              ref="timingLogsCollapse"
              class="collapsable"
              :class="{ 'mb-1': !liveJobData.record }"
              title="Timing Logs"
            >
              <template v-slot:default="{ visible }">
                <general-timing-logs :visible="visible" />
              </template>
            </collapsable>

            <collapsable
              v-if="liveJobData.record"
              class="collapsable"
              :class="{ 'mb-1': liveJobData.record }"
              title="Records"
            >
              <b-table
                ref="table"
                :items="liveJobData.records"
                show-empty
                :fields="recordFields"
                responsive
                class="loaded border-default border-b border-r border-l rounded"
              >
                <template #cell(path)="data">
                  <a
                    v-if="data.item.uploaded"
                    class="link"
                    @click="openStorageRecords(data.value)"
                  >
                    {{ data.value }}
                    <GjIcon
                      name="Redirect"
                      size="20"
                    />
                  </a>
                  <span v-else>{{ data.value }}</span>
                </template>
              </b-table>
            </collapsable>

            <collapsable
              class="mb-1 collapsable"
              title="Authorization"
            >
              <encoder-authorization-settings v-model="liveJobData" />

              <div class="d-flex justify-content-end">
                <button-loading
                  :spinning="updatingLiveJob"
                  :disabled="updatingLiveJob"
                  variant="bg-primary"
                  @click="updateJob"
                >
                  Update
                </button-loading>
              </div>
            </collapsable>
          </div>
        </paper>
      </div>
    </div>
  </parent>
</template>
<script>
import {
  BIconRecordCircle,
  BIconCircleFill,
  // BIconScissors,
} from 'bootstrap-vue'
import Paper from '@/components/paper/Paper.vue'
import Collapsable from '@/components/Collapsable.vue'
import TextHidden from '@/components/text/TextHidden.vue'
import VpPlayer from '@/components/vp-player/Player.vue'
import EncoderAuthorizationSettings from '@/components/views/encoder/EncoderAuthorizationSettings.vue'
import redirectError from '@/services/redirectError'
import JobStatistics from './JobStatistics.vue'
import OutputGroup from './helpers/OutputGroup.vue'
import GeneralTimingLogs from './helpers/GeneralTimingLogs.vue'

export default {
  name: 'LiveJobSetting',
  components: {
    Paper,
    JobStatistics,
    BIconCircleFill,
    Collapsable,
    // BIconScissors,
    BIconRecordCircle,
    OutputGroup,
    GeneralTimingLogs,
    TextHidden,
    VpPlayer,
    EncoderAuthorizationSettings,
  },
  data() {
    return {
      jobName: '',
      playerReady: false,
      liveJobData: null,
      showKey: false,
      fields: ['key', 'value'],
      recordFields: ['path'],
      showPassword: false,
      showCutControls: false,
    }
  },
  computed: {
    liveJobCuttedVideo() {
      return this.$store.state.liveEncoder.liveJobCuttedVideo
    },

    playerAvailable() {
      if (!this.liveJobData.status) {
        return false
      }
      return (
        this.liveJobData.status === this.liveJobData.liveJobStatusType.Processing
        || this.liveJobData.status
          === this.liveJobData.liveJobStatusType.Offline
      )
    },
    destinationStatus() {
      if (!this.liveJobData) return false
      return (
        this.liveJobData.status
          === this.liveJobData.liveJobStatusType.Pending
        || this.liveJobData.status
          === this.liveJobData.liveJobStatusType.Processing
      )
    },
    gettingLiveJob() {
      return this.$store.state.liveEncoder.gettingLiveJob
    },
    updatingLiveJob() {
      return this.$store.state.liveEncoder.updatingLiveJob
    },
    liveJob() {
      return this.$store.state.liveEncoder.liveJob
    },
    liveJobUrl() {
      if (this.liveJobData.flvDisplay !== '') {
        return this.liveJobData.flvDisplay
      }
      return null
    },
    getMasterFilePath() {
      const foundGroup = this.liveJobData.groups.find(group => group.name === 'hls' || group.name === 'AppleHLS')
      return foundGroup ? foundGroup.masterFilePath : null
    },
    filePathComputed() {
      return this.getMasterFilePath || null
    },
    liveJobKey() {
      const key = this.liveJobData?.key
      if (!key) return '-'
      if (this.liveJobData?.hasRelay) return String(key)
      return String(key).toUpperCase()
    },
    primaryEndpoint() {
      return this.liveJobData ? this.liveJobData.primaryEndpoint : null
    },
    storageMasterFile() {
      return this.liveJobData ? this.liveJobData.storageMasterFile : null
    },
    jobStatistics() {
      if (this.liveJobData?.statistics) {
        return this.liveJobData.statistics
      }
      return false
    },
    location() {
      return this.liveJobData ? this.liveJobData.records.path : null
    },
    destination() {
      return this.liveJobData?.destination || null
    },
    jobStatusClass() {
      if (
        this.liveJobData?.status
          === this.liveJobData?.liveJobStatusType.Pending
        || this.liveJobData?.status
          === this.liveJobData?.liveJobStatusType.Processing
      ) {
        return 'text-warning'
      }
      if (
        this.liveJobData?.status === this.liveJobData?.liveJobStatusType.Live
      ) {
        return 'text-success'
      }
      if (
        this.liveJobData?.status
          === this.liveJobData?.liveJobStatusType.Offline
        || this.liveJobData?.status
          === this.liveJobData?.liveJobStatusType.NotApplicable
        || this.liveJobData?.status === this.liveJobData?.liveJobStatusType.Error
      ) {
        return 'text-danger'
      }
      return ''
    },
    settingTranscodingStatus() {
      return this.$store.state.liveEncoder.settingTranscodingStatus
    },
    transcodingStatusIcon() {
      if (this.settingTranscodingStatus) return 'LoaderAlt'
      return this.liveJobData.status === this.liveJobData.liveJobStatusType.Processing
        ? 'Stop_fill'
        : 'Play_fill'
    },
  },

  created() {
    this.fetchLiveJob()

    this.$store.dispatch('organization/fetchCountries').catch(error => {
      this.errorToast(
          error.response?.data?.errors?.toString() || 'Something went wrong while fetching countries',
      )
    })
  },
  methods: {
    trimmVideo(va) {
      this.isHidden = va
    },
    fetchLiveJob() {
      this.jobName = this.$route.params.name
      this.$store
        .dispatch('liveEncoder/getLiveJobV2', this.jobName)
        .then(res => {
          this.liveJobData = res.data?.result
        })
        .catch(err => redirectError(err, this.$router, this.$route.path))
    },
    updateTranscoding() {
      this.jobName = this.$route.params.name
      const version = this.liveJob.livestreamProvider === 1 ? '' : 'V2'
      this.$store
        .dispatch('liveEncoder/updateTranscoding', { name: this.jobName, version })
        .then(() => {
          this.fetchLiveJob()
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while updating transcoding status.',
          )
        })
    },
    openStorageRecords(url) {
      const link = new URL(url)
      const path = link.pathname
      this.openStorage(path.toString().slice(1, path.length))
    },
    openStorage(dest) {
      if (dest) {
        const temp = dest.split('/')
        const bucket = temp[0]
        let key = `${temp.slice(1, temp.length).join('/')}${
          temp.length > 1 ? '/' : ''
        }`
        const regEx = /^[\w,\s-]+\.[A-Za-z]+$/
        if (regEx.test(temp[temp.length - 1])) {
          key = key.slice(0, key.length - 1)
        }
        const destination = this.$router.resolve({
          name: 'object-files',
          params: {
            projectId: this.$route.params.projectId,
            id: bucket,
          },
          query: regEx.test(temp[temp.length - 1])
            ? {
              key,
              tab: 0,
            }
            : {
              key,
            },
        })
        window.open(destination.href, '_blank')
      }
    },
    updateJob() {
      const query = {
        name: this.liveJobData.name,
        liveJob: {
          playbackAuthorizationEndpoint: this.liveJobData.playbackAuthorizationEndpoint,
          allowedCountries: this.liveJobData.allowedCountries,
          disallowedCountries: this.liveJobData.disallowedCountries,
        },
      }
      this.$store
        .dispatch('liveEncoder/updateLiveJob', query)
        .then(() => {
          this.successToast('Live job was updated')
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors?.toString() || 'Something went wrong while updating live job.')
        })
    },
  },
}
</script>
<style lang="scss" scoped>
.live-job-settings {
  max-width: 1920px;
  margin: auto;

  .inner-paper::v-deep {
    .card-header {
      padding: 16px 0;
    }
  }
  .details::v-deep {
    .form-row {
      align-items: center;
    }
  }
}
.label-text {
  margin-bottom: 8px;
  color: #667c99;
}
.info-box {
  height: calc(100% - 32px);
  padding: 12px;
}
.job-status {
  display: flex;
  align-items: center;
  svg {
    margin-left: 5px;
    font-size: 7px;
  }
}
.private-key {
  position: relative;
  span {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: var(--white);
  }
}
.shadow{
  box-shadow: rgba(149, 157, 165, 0.2) 0px 4px 16px !important;
}
.fit-content {
  width: fit-content;
}

.collapsable::v-deep {
  .card-header {
    padding: 16px 0 !important;
  }
  .collapse {
    .card-body {
      padding: 16px 0 !important;
    }
  }
}
</style>
